import { useCallback } from "react";
import useAgentStore from "../useAgentStore";
import {
  AI_AGENT_BOOTSTRAP_SYSTEM_PROMPT,
  AI_AGENT_DEFAULT_DESCRIPTION,
  AI_AGENT_DEFAULT_NAME,
  CREATE_STAGE_CREATING_AGENT,
  CREATE_STAGE_IDLE,
  CREATE_STAGE_STARTING_CHAT,
  buildSelfConfigurationKickoffMessage,
} from "./agentCreation";
import { normalizeKnowledgeIndexStatus } from "./knowledgeStatus";
import {
  SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT,
  ensureSelfConfigurationToolsForMode,
} from "./selfConfiguration";
import { extractSaveErrorMessage } from "./toolConfigValidation";

export function useAgentConfigSave({
  isCreateMode,
  isAiCreatePath,
  agentId,
  onCreated,
  onSaved,
  setSaving,
  setCreationStage,
  setCreationError,
  setInboxAddress,
  setKnowledgeIndexStatus,
  flushPendingAvatarUpload,
  applyPendingAgentFunctionBindings,
}) {
  const persistAgentConfig = useCallback(
    async ({
      data,
      normalizedAiRequirements,
      showResult,
      showFailureResult,
    }) => {
      try {
        if (isCreateMode) {
          if (isAiCreatePath) {
            const aiCreateData = {
              ...data,
              name: AI_AGENT_DEFAULT_NAME,
              description:
                data.description?.trim() || AI_AGENT_DEFAULT_DESCRIPTION,
              systemPrompt:
                data.systemPrompt?.trim() || AI_AGENT_BOOTSTRAP_SYSTEM_PROMPT,
              selfConfigurationMode: SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT,
              enabledTools: ensureSelfConfigurationToolsForMode(
                data.enabledTools,
                SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
              ),
            };

            setCreationStage(CREATE_STAGE_CREATING_AGENT);
            const created = await useAgentStore
              .getState()
              .createAgent(aiCreateData);
            if (!created?.agentId) {
              setSaving(false);
              setCreationStage(CREATE_STAGE_IDLE);
              const message = extractSaveErrorMessage(
                null,
                useAgentStore.getState().error ||
                  "We couldn't create the agent. Please try again."
              );
              setCreationError(message);
              showResult("error", message);
              return;
            }

            if (flushPendingAvatarUpload) {
              try {
                await flushPendingAvatarUpload(created.agentId);
              } catch (error) {
                setSaving(false);
                setCreationStage(CREATE_STAGE_IDLE);
                const message = extractSaveErrorMessage(
                  error,
                  "Agent created, but the avatar upload failed."
                );
                setCreationError(message);
                showResult("error", message);
                if (onCreated) onCreated(created);
                return;
              }
            }

            setCreationStage(CREATE_STAGE_STARTING_CHAT);
            const kickoffMessage =
              buildSelfConfigurationKickoffMessage(normalizedAiRequirements) ||
              normalizedAiRequirements;
            const started = await useAgentStore
              .getState()
              .startChat(created.agentId, kickoffMessage);

            setSaving(false);
            setCreationStage(CREATE_STAGE_IDLE);
            if (created?.knowledgeIndexStatus) {
              setKnowledgeIndexStatus(
                normalizeKnowledgeIndexStatus(created.knowledgeIndexStatus)
              );
            }
            if (started?.chatSessionId) {
              if (onCreated) {
                onCreated({
                  ...created,
                  chatSessionId: started.chatSessionId,
                });
              }
              showResult("success");
            } else {
              const message =
                "Agent created, but we couldn't start the first chat automatically. Opening the agent so you can continue manually.";
              setCreationError(message);
              showResult("error", message);
              setTimeout(() => {
                if (onCreated) onCreated(created);
              }, 900);
            }
            return;
          }

          const created = await useAgentStore.getState().createAgent(data);
          if (created?.agentId && flushPendingAvatarUpload) {
            try {
              await flushPendingAvatarUpload(created.agentId);
            } catch (error) {
              setSaving(false);
              showFailureResult(
                error,
                "Agent created, but the avatar upload failed."
              );
              if (onCreated) onCreated(created);
              return;
            }
          }
          if (created?.agentId && applyPendingAgentFunctionBindings) {
            await applyPendingAgentFunctionBindings(created.agentId);
          }
          setSaving(false);
          if (created) {
            if (created.inboxAddress) setInboxAddress(created.inboxAddress);
            if (created.knowledgeIndexStatus) {
              setKnowledgeIndexStatus(
                normalizeKnowledgeIndexStatus(created.knowledgeIndexStatus)
              );
            }
            if (onCreated) onCreated(created);
            showResult("success");
          } else {
            showFailureResult(
              null,
              useAgentStore.getState().error ||
                "We couldn't create the agent. Please try again."
            );
          }
        } else {
          const updated = await useAgentStore
            .getState()
            .updateAgent(agentId, data);
          setSaving(false);
          if (updated) {
            if (updated.inboxAddress) setInboxAddress(updated.inboxAddress);
            if (updated.knowledgeIndexStatus) {
              setKnowledgeIndexStatus(
                normalizeKnowledgeIndexStatus(updated.knowledgeIndexStatus)
              );
            }
            if (onSaved) onSaved(updated);
            showResult("success");
          } else {
            showFailureResult(
              null,
              useAgentStore.getState().error ||
                "We couldn't save changes. Check required tool settings and try again."
            );
          }
        }
      } catch (error) {
        setSaving(false);
        setCreationStage(CREATE_STAGE_IDLE);
        const message = extractSaveErrorMessage(
          error,
          useAgentStore.getState().error
        );
        if (isAiCreatePath) {
          setCreationError(message);
        }
        showResult("error", message);
      }
    },
    [
      agentId,
      isAiCreatePath,
      isCreateMode,
      onCreated,
      onSaved,
      setCreationError,
      setCreationStage,
      setInboxAddress,
      setKnowledgeIndexStatus,
      setSaving,
      flushPendingAvatarUpload,
      applyPendingAgentFunctionBindings,
    ]
  );

  return { persistAgentConfig };
}
